import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  TableOptions,
  Row,
  PaginationState,
  getPaginationRowModel,
  ExpandedState,
  RowSelectionState,
} from "@tanstack/react-table";
import {
  Dispatch,
  Fragment,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getColumnDefs } from "../utils/tableUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faMinus,
  faPlus,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/pro-solid-svg-icons";
import { t } from "i18next";
import { useMediaBreakpoint } from "@unleashed/hooks/useMediaBreakpoint";
import classNames from "classnames";
import { DataNotFound } from "../../core/components/DataNotFound";
import { Trans } from "react-i18next";
import { TableHeaderProps } from "./TableHeader";
import ReactPaginate from "react-paginate";

interface TableProps<T>
  extends Omit<
    TableOptions<T>,
    | "data"
    | "columns"
    | "state"
    | "enableRowSelection"
    | "onExpandedChange"
    | "onPaginationChange"
    | "onRowSelectionChange"
    | "getCoreRowModel"
    | "getSortedRowModel"
    | "getPaginationRowModel"
  > {
  data: T[];
  expandedComponent?: (row: Row<T>) => ReactElement | ReactElement[];
  children: ReactElement | (ReactElement | boolean)[];
  className?: string;
  expandedState?: ExpandedState;
  isPrinting?: boolean;
  enforceColumnsWidth?: boolean;
  hideHeaderRow?: boolean;
  addEmptyExpanderSpace?: boolean;
  rowSelectionState?: [
    RowSelectionState,
    Dispatch<SetStateAction<RowSelectionState>>
  ];
  wrapCells?: true;
  getRowColor?: (row: T) => string;
  elementType?:string;
  total?:number;
  minWidth?: number;
  getRowId?: (row: T) => string;
  pageCount?: number;
  onPaginationChange?: (pagination: PaginationState) => void;
  manualPagination?: boolean;
}

const paginationData = [
  { pageSize: 15, display: "15" },
  { pageSize: 25, display: "25" },
  { pageSize: 50, display: "50" },
  { pageSize: 100, display: "100" },
  { pageSize: 1000, display: "1000" },
];

export const Table = <T,>({
  data,
  children,
  className,
  addEmptyExpanderSpace = false,
  expandedComponent,
  expandedState,
  hideHeaderRow = false,
  enableSorting = true,
  isPrinting = false,
  enforceColumnsWidth = false,
  rowSelectionState,
  wrapCells,
  getRowColor,
  elementType,
  total,
  minWidth,
  getRowId,
  ...rest
}: TableProps<T>) => {
  const defaultMinWidth = 25;
  const expanderMinWidth = minWidth !== undefined ? minWidth : defaultMinWidth;
  const columns = useMemo(() => {
    if (expandedComponent)
      return [{
        id: "expander",
        size: expanderMinWidth,
        header: () => null,
        cell: ({row} : { row: Row<T> }) => {
          return (<span
            {...{
              style: {cursor: "pointer", minWidth: `${expanderMinWidth}px`},
            }}
            role="button"
            tabIndex={0}
            onClick={() => row.getIsExpanded() ? row.toggleExpanded(false) : row.toggleExpanded(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (row.getIsExpanded()) { row.toggleExpanded(false); } else { row.toggleExpanded(true); }
              }
            }}
          >
            {row.getIsExpanded() ?
              <FontAwesomeIcon icon={faMinus} className="text-gray-500 bg-slate-200 rounded-sm border-gray-400 border w-3.5 h-3.5"/> :
              <FontAwesomeIcon icon={faPlus} className="text-gray-500 bg-slate-200 rounded-sm border-gray-400 border w-3.5 h-3.5"/>
            }
          </span>);
        },
      }, ...getColumnDefs<T>(children)];
    else {
      if (addEmptyExpanderSpace)
        return [{
          id: "expander",
          size: 25,
          header: () => null,
          cell: () => {
            return (<span
              {...{
                style: {minWidth: `${expanderMinWidth}px`},
              }}
              role="button"
              tabIndex={0}
            >&nbsp;</span>);
          },
        }, ...getColumnDefs<T>(children)];
      else
        return getColumnDefs<T>(children);
    }

  }, [children, expandedComponent, addEmptyExpanderSpace, expanderMinWidth]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const breakpoint = useMediaBreakpoint();
  const [expanded, setExpanded] = useState<ExpandedState>(expandedState ?? {});
  const [rowSelection, setRowSelection] = rowSelectionState ?? [
    undefined,
    undefined,
  ];

  const table = useReactTable({
    ...rest,
    data,
    columns,
    ...(getRowId ? { getRowId } : {}),
    state: {
      pagination: isPrinting
        ? {
          pageIndex: 0,
          pageSize: 99999,
        }
        : pagination,
      expanded,
      rowSelection,
    },
    enableSorting,
    enableRowSelection: !!rowSelection,
    onExpandedChange: setExpanded,
    onPaginationChange: rest.manualPagination
      ? (updater) => {
        const newPagination = updater instanceof Function ? updater(pagination) : updater;
        setPagination(newPagination);
        rest.onPaginationChange?.(newPagination);
      }
      : setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: rest.manualPagination ? undefined : getPaginationRowModel(),
    // server-side pagination
    manualPagination: rest.manualPagination ?? false,
    pageCount: rest.manualPagination ? (rest.pageCount ?? -1) : undefined,
  });

  useEffect(() => {
    setExpanded(expandedState ?? {});
  }, [expandedState]);

  return (
    <div className={classNames("overflow-auto", className)}>
      <table
        className={classNames(
          "border border-gray-400 w-full",
          wrapCells ? "" : "min-w-max"
        )}
      >
        {!hideHeaderRow && <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className={classNames("text-left bg-gray-300")}
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={enforceColumnsWidth && header.getSize() > 0 ? {minWidth: `${header.getSize()}px`, maxWidth: `${header.getSize()}px`} : {}}
                  className={classNames(
                    "border-b border-gray-400 text-sm p-2 whitespace-nowrap",
                    `text-${
                      (header.column.columnDef as TableHeaderProps<T>).align ??
                      "left"
                    }`
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <>
                      <div className="hidden print:block">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                      <button
                        type="button"
                        className={classNames(
                          "print:hidden",
                          header.column.getCanSort()
                            ? "select-none"
                            : "cursor-default"
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <FontAwesomeIcon
                              icon={faSortUp}
                              className="ml-4 text-gray-500"
                            />
                          ),
                          desc: (
                            <FontAwesomeIcon
                              icon={faSortDown}
                              className="ml-4 text-gray-500"
                            />
                          ),
                        }[header.column.getIsSorted() as string] ??
                          (header.column.getCanSort() ? (
                            <FontAwesomeIcon
                              icon={faSort}
                              className="ml-4 text-gray-500"
                            />
                          ) : null)}
                      </button>
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        }
        {data.length ? (
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  className={classNames(
                    row.getIsExpanded() ? "sticky top-0 z-30 border-b-0" : "",
                    "border border-gray-400",
                    getRowColor ? getRowColor(row.original) : "bg-white"
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    let style = {};
                    let hasTextOverflow = false;
                    if (enforceColumnsWidth && cell.column.getSize() > 0) {
                      style = {
                        ...style,
                        minWidth: `${cell.column.getSize()}px`,
                        maxWidth: `${cell.column.getSize()}px`
                      };
                    }
                    if ((cell.column.columnDef as TableHeaderProps<T>).overflow === "ellipsis") {
                      hasTextOverflow = true;
                    }

                    return (
                      <td
                        key={cell.id}
                        style={style}
                        className={classNames(
                          "px-2 py-1 bg-white/75",
                          hasTextOverflow ? "whitespace-nowrap overflow-hidden overflow-ellipsis" : "",
                          `text-${
                            (cell.column.columnDef as TableHeaderProps<T>)
                              .align ?? "left"
                          }`,
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>);
                  })}
                </tr>
                {row.getIsExpanded() && expandedComponent && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length}>
                      {expandedComponent(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td align="center" colSpan={99}>
                <DataNotFound />
              </td>
            </tr>
          </tbody>
        )}

        {(table.getPageCount() > 1 || data.length > 15 || (elementType && total)) && (
          <tfoot>
            <tr
              className={classNames("text-left bg-gray-300 text-sm print:hidden")}>
              <td colSpan={table.getAllColumns().length}
                className="text-center p-2">
                <div className="flex justify-between items-center">
                  {(elementType && total) && (
                    <p className="absolute text-gray-700 font-bold text-sm">
                      <Trans>
                      Showing {`${table.getRowModel().rows.length} ${elementType} `}of{` ${total}`}
                      </Trans>
                    </p>
                  )}

                  <div className="flex flex-1 justify-center">
                    <ReactPaginate
                      breakLabel="..."
                      nextLabel={
                        <>
                          <Trans>Next</Trans>
                          <FontAwesomeIcon className="ml-3" icon={faArrowRight} />
                        </>
                      }
                      onPageChange={(i) => {
                        if (rest.manualPagination) {
                          // server-side pagination
                          const newPagination = {
                            pageIndex: i.selected,
                            pageSize: pagination.pageSize
                          };

                          setPagination(newPagination);

                          if (rest.onPaginationChange) {
                            rest.onPaginationChange(newPagination);
                          }
                        } else {
                          // client-side pagination
                          table.setPageIndex(i.selected);
                        }
                      }}
                      pageRangeDisplayed={breakpoint === "sm" ? 0 : 1}
                      marginPagesDisplayed={breakpoint === "sm" ? 0 : 2}
                      pageCount={table.getPageCount()}
                      forcePage={pagination.pageIndex}
                      previousLabel={
                        <>
                          <FontAwesomeIcon className="mr-3" icon={faArrowLeft} />
                          <Trans>Previous</Trans>
                        </>
                      }
                      renderOnZeroPageCount={undefined}
                      className="flex items-center h-10 text-sm select-none"
                      breakClassName="w-10 px-0.5 text-center"
                      previousLinkClassName="flex items-center mr-2"
                      nextLinkClassName="flex items-center ml-2"
                      disabledLinkClassName="text-gray-500 cursor-default"
                      activeLinkClassName="bg-[#eeee22]/50"
                      pageLinkClassName="flex items-center justify-center h-10 w-10 rounded-full"
                    />

                    <div className="ml-6">
                      <div>
                        <select
                          value={pagination.pageSize}
                          onChange={(e) => {
                            const newPageSize = Number(e.target.value);

                            if (rest.manualPagination) {
                              const newPagination = {
                                pageIndex: 0, // reset to first page
                                pageSize: newPageSize
                              };

                              setPagination(newPagination);

                              if (rest.onPaginationChange) {
                                rest.onPaginationChange(newPagination);
                              }
                            } else {
                              table.setPageSize(newPageSize);
                              table.setPageIndex(0);
                            }
                          }}
                          className={classNames(
                            "block py-[5px] bg-clip-padding bg-no-repeat w-full bg-transparent border-0",
                            "border-b-2 focus:outline-none focus:ring-0 peer",
                            "focus:border-primary border-gray-400"
                          )}
                        >
                          {paginationData.map((page, i) => (
                            <option
                              key={i}
                              className="relative cursor-pointer select-none text-left pl-2"
                              value={page.pageSize}
                            >
                              {t("Show {{pageSize}}", { pageSize: page.display })}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};
